import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TeamProgressBar } from '../../ui/components';
import { Table, TableVersion, TextLink, TextLinkStyle, TextLinkIconPosition } from '../../ui/componentsV2';
import type { ColumnsInterface } from '../../ui/componentsV2/Table';
import { Theme, MLPVersion } from '../../ui/componentsV2/types/enums';
import { computeTeamProgress, isMemberLocked } from '../../ui/utils/teamProgress';
import { DynamicObjectInterface } from '../../sdk/type';
import styles from './Homepage.module.scss';

interface HomeTeamTablePropTypes {
  teams: DynamicObjectInterface[];
  // Fallback sub-challenge list (used when a team row doesn't carry its own).
  subChallenges: DynamicObjectInterface[];
}

const HomeTeamTable = ({ teams, subChallenges }: HomeTeamTablePropTypes) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Union of configured check-in days across the sub-challenges this team's members
  // belong to. Empty => check-in step stays a grey placeholder for this team.
  // Prefers the team's own campaign sub-challenges (merged multi-campaign table).
  // The challenge is over for this team once its end date has passed. After this,
  // incomplete steps can no longer be worked on (shown red / incomplete).
  const isChallengeEnded = (team: DynamicObjectInterface): boolean => {
    const end = team?.teamEndDate ?? team?.campaignEnd;
    if (!end) return false;
    const endDate = new Date(end);
    if (isNaN(endDate.getTime())) return false;
    endDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.getTime() > endDate.getTime();
  };

  const teamCheckInDays = (team: DynamicObjectInterface): (number | string)[] => {
    const ids = new Set(
      (team?.participants ?? [])
        .map((p: DynamicObjectInterface) => p?.subChallengeId)
        .filter((id: string | undefined) => !!id),
    );
    const scList = team?.campaignSubChallenges?.length ? team.campaignSubChallenges : subChallenges;
    const days = new Set<number>();
    (scList ?? []).forEach((sc: DynamicObjectInterface) => {
      if (ids.has(sc.subChallengeId)) {
        (sc?.checkIn?.days ?? []).forEach((d: number | string) => {
          const n = Number(d);
          if (!isNaN(n) && n > 0) days.add(n);
        });
      }
    });
    return Array.from(days);
  };

  const columns: ColumnsInterface[] = [
    {
      key: 'teams',
      text: t('dashboard.home.label.columnTeams'),
      width: 25,
      render: (team: DynamicObjectInterface) => (
        <div className={styles['home-table-team-name']}>{team?.teamName ?? team?.name}</div>
      ),
    },
    {
      key: 'challenges',
      text: t('dashboard.home.label.columnChallenges'),
      width: 25,
      render: (team: DynamicObjectInterface) => {
        if (!team?.campaignName) {
          return <span className={styles['home-table-empty']}>{t('dashboard.my-team.label.dateNotAvailable')}</span>;
        }
        return <div className={styles['home-table-challenge-name']}>{team.campaignName}</div>;
      },
    },
    {
      key: 'progress',
      text: t('dashboard.home.label.columnProgress'),
      width: 35,
      render: (team: DynamicObjectInterface) => (
        <TeamProgressBar
          progress={computeTeamProgress(team, teamCheckInDays(team))}
          locked={isMemberLocked(team)}
          campaignEnded={isChallengeEnded(team)}
        />
      ),
    },
    {
      key: 'action',
      text: t('dashboard.home.label.columnAction'),
      width: 20,
      render: (team: DynamicObjectInterface) => (
        <div className={styles['home-table-actions']}>
          <TextLink
            text={t('dashboard.home.label.myProgress')}
            theme={Theme.BLUE}
            textLinkStyle={TextLinkStyle.ICON}
            iconPosition={TextLinkIconPosition.RIGHT}
            rightIcon={{ iconName: 'chevron-right' }}
            onClick={() => navigate(`/campaigns/${team?.campaignId}?tId=${team?.teamId}`)}
          />
          <TextLink
            text={t('dashboard.home.label.viewTeam')}
            theme={Theme.BLUE}
            textLinkStyle={TextLinkStyle.ICON}
            iconPosition={TextLinkIconPosition.RIGHT}
            rightIcon={{ iconName: 'chevron-right' }}
            onClick={() => navigate(`/campaigns/${team?.campaignId}/myTeam`)}
          />
        </div>
      ),
    },
  ];

  // One action pair (My Progress / View Team) — reused by table + mobile cards.
  const renderActions = (team: DynamicObjectInterface) => (
    <div className={styles['home-table-actions']}>
      <TextLink
        text={t('dashboard.home.label.myProgress')}
        theme={Theme.BLUE}
        textLinkStyle={TextLinkStyle.ICON}
        iconPosition={TextLinkIconPosition.RIGHT}
        rightIcon={{ iconName: 'chevron-right' }}
        onClick={() => navigate(`/campaigns/${team?.campaignId}?tId=${team?.teamId}`)}
      />
      <TextLink
        text={t('dashboard.home.label.viewTeam')}
        theme={Theme.BLUE}
        textLinkStyle={TextLinkStyle.ICON}
        iconPosition={TextLinkIconPosition.RIGHT}
        rightIcon={{ iconName: 'chevron-right' }}
        onClick={() => navigate(`/campaigns/${team?.campaignId}/myTeam`)}
      />
    </div>
  );

  return (
    <>
      {/* Table on web/tablet; pivoted cards on phone (toggled via CSS). */}
      <div className={styles['home-table-desktop']}>
        <Table
          theme={Theme.BLACK}
          tableVersion={TableVersion.ADMIN}
          mlpVersion={MLPVersion.TWO}
          columns={columns}
          dataSource={teams}
          rowKey="teamId"
          pagination={teams.length > 6}
          className={styles['home-team-table']}
        />
      </div>

      <div className={styles['home-cards']}>
        {teams.map((team: DynamicObjectInterface) => (
          <div key={team.teamId} className={styles['home-card']}>
            <div className={styles['home-card-title']}>{team?.teamName ?? team?.name}</div>
            <div className={styles['home-card-row']}>
              <span className={styles['home-card-label']}>{t('dashboard.home.label.columnChallenges')}</span>
              <span className={styles['home-card-value']}>
                {team?.campaignName ?? t('dashboard.my-team.label.dateNotAvailable')}
              </span>
            </div>
            <div className={styles['home-card-progress']}>
              <TeamProgressBar
                progress={computeTeamProgress(team, teamCheckInDays(team))}
                locked={isMemberLocked(team)}
                campaignEnded={isChallengeEnded(team)}
              />
            </div>
            {renderActions(team)}
          </div>
        ))}
      </div>
    </>
  );
};

export default HomeTeamTable;
